import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WorkflowGraphStore } from '../../store/workflow-graph.store';
import { WorkflowEditorService } from '../../store/workflow-editor.service';
import { WORKFLOW_NODE_TYPE_META } from '../../models/node-catalog';
import { WorkflowChecklistItem } from '../../../models/workflow-testing.model';

interface NodeIssue {
  nodeKey: string;
  nodeName: string;
  message: string;
}

/**
 * Barra inferior que explica POR QUÉ el workflow todavía no se puede activar (Fase 4).
 *
 * Se calca de la `validation-bar` de chatflow y combina las DOS fuentes de verdad de workflow:
 * - Errores de validación de NODO del store (`nodeErrors`): accionables, con clic para enfocar
 *   el nodo problemático en el canvas.
 * - El CHECKLIST de activación del backend (`editor.checklist`): la puerta de activación
 *   (estructura, disparador, referencias de email, settings de inscripción…), con ítems no
 *   cumplidos (FAILED) y advertencias (WARNING).
 *
 * Reactividad SOLO por signals (gotcha §6). OnPush. No bloquea Guardar; es informativa.
 */
@Component({
  selector: 'app-workflow-validation-bar',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible()) {
      <div class="border-t border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <button
          type="button"
          class="flex w-full items-center gap-2 px-4 py-2 text-left transition hover:bg-gray-50 dark:hover:bg-white/5"
          (click)="expanded.set(!expanded())">
          @if (blocked()) {
            <i class="fa-solid fa-triangle-exclamation text-xs text-warning-500"></i>
            <span class="text-xs font-medium text-gray-700 dark:text-gray-300">
              {{ problemCount() }} {{ problemCount() === 1 ? 'motivo' : 'motivos' }} por los que no se puede activar
            </span>
          } @else {
            <i class="fa-solid fa-circle-check text-xs text-success-500"></i>
            <span class="text-xs font-medium text-gray-700 dark:text-gray-300">
              El workflow está listo para activar.
            </span>
            @if (warnings().length) {
              <span class="text-[11px] text-amber-600 dark:text-amber-400">
                ({{ warnings().length }} advertencia{{ warnings().length === 1 ? '' : 's' }})
              </span>
            }
          }
          <span class="flex-1"></span>
          <i class="fa-solid text-[10px] text-gray-400" [class.fa-chevron-up]="expanded()" [class.fa-chevron-down]="!expanded()"></i>
        </button>

        @if (expanded()) {
          <div class="max-h-52 overflow-y-auto border-t border-gray-100 dark:border-gray-800">
            <!-- Problemas por paso: accionables, enfocan el nodo. -->
            @if (nodeIssues().length) {
              <p class="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Revisa estos pasos
              </p>
              @for (issue of nodeIssues(); track issue.nodeKey + issue.message) {
                <button
                  type="button"
                  class="flex w-full items-start gap-2 px-4 py-1.5 text-left transition hover:bg-gray-50 dark:hover:bg-white/5"
                  (click)="focusNode.emit(issue.nodeKey)">
                  <span class="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500"></span>
                  <span class="min-w-0 flex-1">
                    <span class="text-[11px] text-gray-600 dark:text-gray-300">{{ issue.message }}</span>
                    <span class="ml-1 text-[10px] text-brand-500">— ir al paso «{{ issue.nodeName }}»</span>
                  </span>
                </button>
              }
            }

            <!-- Checklist de activación del backend. -->
            @if (checklistItems().length) {
              <p class="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Checklist de activación
              </p>
              @for (item of checklistItems(); track $index) {
                <div class="flex items-start gap-2 px-4 py-1.5">
                  <i class="mt-0.5 text-[11px]" [ngClass]="iconClass(item)"></i>
                  <span class="min-w-0 flex-1">
                    <span class="text-[11px] text-gray-700 dark:text-gray-200">{{ item.label }}</span>
                    @if (item.message && item.status !== 'PASSED') {
                      <span class="block text-[10px] text-gray-500 dark:text-gray-400">{{ item.message }}</span>
                    }
                  </span>
                </div>
              }
            }
          </div>
        }
      </div>
    }
  `
})
export class WorkflowValidationBarComponent {
  private readonly store = inject(WorkflowGraphStore);
  private readonly editor = inject(WorkflowEditorService);

  /** Clic en un problema de paso → el canvas centra y selecciona el nodo culpable. */
  readonly focusNode = output<string>();

  protected readonly expanded = signal(true);

  /** Errores de validación agrupados por nodo → uno por mensaje, con nombre legible. */
  protected readonly nodeIssues = computed<NodeIssue[]>(() => {
    const issues: NodeIssue[] = [];
    for (const [nodeKey, errors] of this.store.nodeErrors()) {
      const node = this.store.findNode(nodeKey);
      const nodeName = node ? node.title.trim() || WORKFLOW_NODE_TYPE_META[node.nodeType].label : nodeKey;
      for (const error of errors) {
        issues.push({ nodeKey, nodeName, message: error.message });
      }
    }
    return issues;
  });

  protected readonly checklistItems = computed<WorkflowChecklistItem[]>(() => this.editor.checklist()?.items ?? []);

  protected readonly warnings = computed(() =>
    this.checklistItems().filter((item) => item.status === 'WARNING')
  );

  private readonly blockers = computed(() =>
    this.checklistItems().filter((item) => item.status === 'FAILED')
  );

  /** No se puede activar: el backend lo dice, o hay errores de nodo sin resolver. */
  protected readonly blocked = computed(() => {
    const canActivate = this.editor.checklist()?.canActivate;
    return canActivate === false || this.nodeIssues().length > 0;
  });

  protected readonly problemCount = computed(() => this.nodeIssues().length + this.blockers().length);

  /** Se muestra cuando ya hay algo que reportar (validación ejecutada o checklist cargado). */
  protected readonly visible = computed(
    () => !!this.editor.checklist() || !!this.store.validation() || this.nodeIssues().length > 0
  );

  protected iconClass(item: WorkflowChecklistItem): string {
    switch (item.status) {
      case 'PASSED':
        return 'fa-solid fa-circle-check text-success-500';
      case 'FAILED':
        return 'fa-solid fa-circle-xmark text-rose-500';
      case 'WARNING':
        return 'fa-solid fa-triangle-exclamation text-warning-500';
      default:
        return 'fa-solid fa-circle-minus text-gray-400';
    }
  }
}
